import { faExternalLinkAlt } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import { Button, type ButtonProps } from "components/Theme/button";
import { useSelectedCompanyId } from "hooks/useCompany";
import { useSelectedScheme } from "hooks/useScheme";
import type React from "react";
import { Link, type Path, type To } from "react-router-dom";
import { usePrivacyMode } from "reducers/privacy";
import { getUrlHash } from "./URL";

interface Props {
  to: Partial<Path>;
  children: React.ReactNode;
  dataCy?: string;
  className?: string;
  onClick?: React.MouseEventHandler<any>;
  external?: boolean;
  onlyExternal?: boolean;
  inline?: boolean;
  colorClassName?: string;
  retainCompany?: boolean;
  button?: ButtonProps;
  /**
   * Optional company_id to use in the URL hash.
   * When provided, this overrides the selected company from global state.
   * This is useful for entity detail links to ensure the correct company context
   * is used, especially when the "ALL" company filter is selected.
   */
  company_id?: string;
}

function CompanyNavLink(props: Props): React.ReactNode {
  const selectedCompanyId = useSelectedCompanyId();
  const scheme = useSelectedScheme();
  const privacyMode = usePrivacyMode();

  const { retainCompany = true, company_id } = props;

  // Use provided company_id if available and not empty, otherwise fall back to selected company
  const effectiveCompanyId =
    company_id && company_id !== "" ? company_id : selectedCompanyId;

  const to: To = {
    ...props.to,
    hash: retainCompany
      ? getUrlHash(effectiveCompanyId, scheme, privacyMode)
      : "",
  };

  const { colorClassName = "text-brand-light-600", inline = true } = props;

  const adjustProps = {
    to: to,
    onClick: props.onClick,
  };

  return (
    <>
      <Link
        {...adjustProps}
        data-cy={props.dataCy}
        target={props.onlyExternal ? "_blank" : undefined}
      >
        {props.button ? (
          <Button {...props.button}>{props.children}</Button>
        ) : (
          <span
            className={clsx(
              props.className,
              colorClassName,
              "px-0",
              inline && "inline-block",
              privacyMode && "blurry-text",
            )}
          >
            {props.children}
          </span>
        )}
      </Link>
      {(props.external || props.onlyExternal) && (
        <Link
          to={to}
          className={clsx(props.className, "mx-1", {
            "d-inline-block": props.inline,
            "blurry-text": privacyMode,
          })}
          target={"_blank"}
        >
          <FontAwesomeIcon icon={faExternalLinkAlt} />
        </Link>
      )}
    </>
  );
}

export default CompanyNavLink;
